import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Crown, Loader2, ArrowRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ScholarshipClaim() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/scholarship-invites/${token}`);
        setInvite(data);
      } catch (e) {
        setErr(e.response?.data?.detail || "Invite not found or expired");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Auto-claim once we have an invite AND a logged-in user.
  useEffect(() => {
    if (!invite || authLoading || claiming || claimed) return;
    if (!user) return; // wait for sign-in
    setClaiming(true);
    (async () => {
      try {
        const { data } = await api.post(`/scholarship-invites/${token}/claim`);
        setClaimed(true);
        if (data?.already_claimed) {
          toast("You already claimed this invite — Pro is active.");
        } else if (data?.applied?.is_scholarship) {
          toast.success("Pro unlocked! You're on a lifetime scholarship 🏆");
        } else {
          toast.success(`Pro unlocked! ${invite.duration_days} days added.`);
        }
      } catch (e) {
        const msg = e.response?.data?.detail || "Could not claim";
        if (e.response?.status === 409) setErr("This invite was already claimed by someone else.");
        else setErr(msg);
      } finally {
        setClaiming(false);
      }
    })();
  }, [invite, user, authLoading, token, claiming, claimed]);

  const goSignUp = () => nav(`/register?next=${encodeURIComponent(`/claim/${token}`)}`);
  const goSignIn = () => nav(`/login?next=${encodeURIComponent(`/claim/${token}`)}`);

  if (loading) return (
    <div className="max-w-lg mx-auto p-6 text-neutral-400 flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
    </div>
  );
  if (err) return (
    <div className="max-w-lg mx-auto p-6">
      <div data-testid="claim-error" className="border border-neutral-800 p-6 text-center">
        <AlertCircle className="w-8 h-8 text-blue-500 mx-auto mb-2" />
        <div className="font-display text-2xl uppercase">Invite unavailable</div>
        <div className="text-sm text-neutral-400 mt-1">{err}</div>
        <Link to="/" className="text-xs text-blue-500 uppercase tracking-widest mt-4 inline-block">Back to home</Link>
      </div>
    </div>
  );

  return (
    <div className="max-w-lg mx-auto p-4 sm:p-6">
      <div className="text-xs tracking-[0.3em] uppercase text-blue-500 font-bold flex items-center gap-2 mb-1">
        <Crown className="w-3 h-3" /> SCHOLARSHIP INVITE
      </div>
      <h1 data-testid="claim-title" className="font-display text-4xl sm:text-5xl uppercase leading-none mb-2">You're invited.</h1>
      <p className="text-sm text-neutral-300 mb-2">
        Coach Grizz hand-picked you for{" "}
        <span className="text-blue-500 font-semibold">
          {invite.duration_days > 0 ? `${invite.duration_days} days of Pro` : "a lifetime Pro scholarship"}
        </span>{" "}
        on GYM with GRIZZ.
      </p>
      {invite.label && <p data-testid="claim-label" className="text-xs text-neutral-500 italic mb-5">"{invite.label}"</p>}

      {claimed ? (
        <div data-testid="claim-success" className="border border-blue-500/50 bg-blue-500/5 p-5">
          <div className="font-display text-3xl uppercase mb-1">You're in. 🔥</div>
          <div className="text-sm text-neutral-300 mb-4">
            {invite.duration_days > 0 ? `Pro is active for ${invite.duration_days} days.` : "Lifetime Pro is yours. Welcome to the squad."}
          </div>
          <Button
            data-testid="claim-go-dashboard"
            onClick={() => nav("/dashboard")}
            className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11"
          >
            Go to Dashboard <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      ) : !user ? (
        <div data-testid="claim-signin-prompt" className="border border-neutral-800 p-5">
          <div className="text-sm text-neutral-300 mb-4">
            Sign up (or sign in) to lock it in. Only takes 30 seconds.
          </div>
          <div className="space-y-2">
            <Button
              data-testid="claim-signup-btn"
              onClick={goSignUp}
              className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11"
            >
              Create account
            </Button>
            <Button
              data-testid="claim-signin-btn"
              onClick={goSignIn}
              variant="outline"
              className="w-full bg-transparent border-neutral-700 text-white rounded-none uppercase tracking-wider font-display h-11"
            >
              I already have an account
            </Button>
          </div>
        </div>
      ) : claiming ? (
        <div className="text-neutral-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Activating Pro…</div>
      ) : null}
    </div>
  );
}
